/**
 * Copyright (c) 2021, Trend Micro, Inc.
 * All rights reserved.
 */
import {
  isSSOV2Enabled,
  isGovCloudRegion,
  RETIRED_SERVICES_TABLE,
  isServiceEntitled,
} from './util';

import { isSSOv2UserLoggedIn, publicRoutes } from './services/helpers';
import { checkMaintenanceModeServiceRestrictions } from './maintenance-mode';

const isWorkloadSignedIn = () =>
  window.sessionStorage.getItem('isWorkloadSignedIn') === 'true' ||
  window.sessionStorage.getItem('rID');

const getServicesList = () =>
  sessionStorage.getItem('C1C_Shell_State')
    ? JSON.parse(sessionStorage.getItem('C1C_Shell_State')).c1Services.c1ServicesList
    : null;

const getWorkloadLicenseMode = (licenseType = '') =>
  sessionStorage.getItem('C1C_Shell_State')
    ? window.location.pathname.startsWith('/workload') &&
      JSON.parse(sessionStorage.getItem('C1C_Shell_State'))?.c1Accounts?.licenseType === licenseType
    : false;

const loadRoute = (key, pathsArr) => pathsArr.find((route) => route === key);
const isUserRescue = localStorage.getItem('WS_RESCUE_LOGGED_IN_USER') === 'true';

export function prefix(location, ...prefixes) {
  return prefixes.some((prefix) => location.href.indexOf(`${location.origin}/${prefix}`) !== -1);
}

export function getParentRoute(pathname) {
  return pathname.split('/').slice(1, 2).join('/');
}

export function unAuthenticatedRoutes(location) {
  if (isSSOV2Enabled()) {
    // We want to check for parent routes only
    const currentRoute = getParentRoute(location.pathname);
    if (loadRoute(currentRoute, publicRoutes)) {
      return true;
    }
    return false;
  }
  return false;
}

export function authenticatedRoutes(location, routes) {
  if (isSSOV2Enabled()) {
    // We want to check for parent routes only
    const currentRoute = getParentRoute(location.pathname);

    if (isSSOv2UserLoggedIn()) {
      if (currentRoute === '') {
        return window.history.pushState({}, '', '/home');
      }
      return loadRoute(currentRoute, routes);
    }
  }
  return false;
}

export function appAuth(location) {
  return Boolean(
    location.pathname === '/' ||
      unAuthenticatedRoutes(location) ||
      // navigation on /support activates the authApp in all regions
      (prefix(location, 'support') && !isGovCloudRegion) ||
      authenticatedRoutes(location, ['admin', 'create-account', 'account-selection', 'onboarding'])
  );
}

export function navbar() {
  return true;
}

/**
 * This function checks if the user is signed in and has a valid license to access the service.
 * In case the license does not exist or is expired, the user is shown a license screen.
 * This check is also used for showing a particular screen during maintenance mode for a service.
 *
 * @param {Location} location
 * @returns {boolean}
 */
export function appLD(location) {
  const isAuthenticated = isWorkloadSignedIn() || isSSOv2UserLoggedIn();

  const shellAccountsPayload = JSON.parse(window.sessionStorage.getItem('C1C_Shell_State'))
    ?.c1Accounts;

  const accountsList = shellAccountsPayload?.c1AccountsList || [];
  const accountID = shellAccountsPayload?.c1AccountID || '';
  const currentAccountRegion = accountsList.find(({ id }) => id === accountID)?.region;

  const servicesList = getServicesList();

  const filteredServiceList = isSSOV2Enabled()
    ? servicesList && servicesList.find((service) => location.pathname.startsWith(`/${service.id}`))
    : servicesList &&
      servicesList.find(
        (service) => location.pathname.startsWith(`/${service.id}`) && service.id !== 'workload'
      );

  const validService = isAuthenticated && filteredServiceList;

  const { license, expired, id } = validService || { id: location.pathname.split('/')[1] };

  const serviceInMaintenanceMode = checkMaintenanceModeServiceRestrictions(
    currentAccountRegion,
    id
  );

  // Show the license screen if the service is retired and the user does not have a valid license.
  if (RETIRED_SERVICES_TABLE[id] === true) {
    return !isServiceEntitled({ id, license, expired });
  }

  if (window.location.pathname.includes('BuyNowModal') || window.location.pathname === '/billing') {
    // [TEMP] We want to give all customers a chance to try the service out for free without needing to request a trial.
    // but entry point to Service is already available from drop-down & URL
    return false;
  } else if (serviceInMaintenanceMode) {
    return true;
  } else if (license === 'unlicensed' || (license === 'trial' && expired === true)) {
    return true;
  } else if (isAuthenticated && (window.location.pathname === '/update-account' || isUserRescue)) {
    // We want to load the landing page if the user is in login rescue mode to prevent routing to other apps.
    return true;
  } else if (
    isAuthenticated &&
    (getWorkloadLicenseMode('azure_saas_metered_billing') ||
      getWorkloadLicenseMode('utility_prepaid'))
  ) {
    return true;
  } else {
    return isAuthenticated && window.location.pathname.startsWith('/home');
  }
}

export function appNP(location) {
  const licenseCheck = appLD(location);

  return licenseCheck === false && prefix(location, 'network');
}

export function appSE(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'application');
}

export function appFSS(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'filestorage');
}

export function appCS(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'container');
}

export function appConformity(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'conformity');
}

export function appSnyk(location) {
  const licenseCheck = appLD(location);
  return licenseCheck === false && prefix(location, 'snyk');
}

export function appMC(location) {
  return Boolean(
    prefix(location, 'administration', 'integrations', 'my-user') &&
      authenticatedRoutes(location, ['administration', 'integrations', 'my-user'])
  );
}

export function appAudit(location) {
  return Boolean(prefix(location, 'audit') && (isSSOv2UserLoggedIn() || isWorkloadSignedIn()));
}

export function appWorkload(location) {
  const licenseCheck = appLD(location);

  return (
    !isUserRescue &&
    ((licenseCheck === false && prefix(location, 'workload')) ||
      location.pathname.startsWith('/com.trendmicro.ds.hostedservice'))
  );
}

export function appMFE(location) {
  return prefix(location, 'mfe-template');
}

export function appSubscription(location) {
  return Boolean(prefix(location, 'billing') && (isSSOv2UserLoggedIn() || isWorkloadSignedIn()));
}

export function appTemplateScanner(location) {
  return Boolean(prefix(location, 'template') && authenticatedRoutes(location, ['template']));
}

export function appC1nx(location) {
  return Boolean(
    prefix(location, 'cloud-one-central') && authenticatedRoutes(location, ['cloud-one-central'])
  );
}
